'use client';

import { defineConfig } from '@chakra-ui/react';

export const config = defineConfig({
  cssVarsPrefix: '',
  theme: {
    tokens: {
      fontSizes: {
        xs: { value: 'calc(12px + var(--text-scale-factor, 0px))' },
        sm: { value: 'calc(14px + var(--text-scale-factor, 0px))' },
        md: { value: 'calc(16px + var(--text-scale-factor, 0px))' },
        lg: { value: 'calc(18px + var(--text-scale-factor, 0px))' },
        xl: { value: 'calc(20px + var(--text-scale-factor, 0px))' },
        '2xl': { value: 'calc(22px + var(--text-scale-factor, 0px))' },
        '3xl': { value: 'calc(24px + var(--text-scale-factor, 0px))' },
        '4xl': { value: 'calc(26px + var(--text-scale-factor, 0px))' },
        '5xl': { value: 'calc(28px + var(--text-scale-factor, 0px))' },
        '6xl': { value: 'calc(30px + var(--text-scale-factor, 0px))' },
        '7xl': { value: 'calc(32px + var(--text-scale-factor, 0px))' },
        '8xl': { value: 'calc(34px + var(--text-scale-factor, 0px))' },
        '9xl': { value: 'calc(36px + var(--text-scale-factor, 0px))' },
      },
      colors: {
        brand: {
          DEFAULT: { value: '#703EFF' },
          100: { value: '#703EFF' },
          80: { value: 'rgba(112, 62, 255, 0.8)' },
          50: { value: 'rgba(112, 62, 255, 0.5)' },
          40: { value: 'rgba(112, 62, 255, 0.4)' },
          30: { value: 'rgba(112, 62, 255, 0.3)' },
          10: { value: 'rgba(112, 62, 255, 0.1)' },
          6: { value: 'rgba(112, 62, 255, 0.06)' },
        },
        /** ================== neutral start =========================== */
        grey: {
          97: { value: '#1A1A1A' },
          95: { value: '#808080' },
          92: { value: '#A3A3A3' },
          88: { value: '#CCCCCC' },
          80: { value: '#E0E0E0' },
          64: { value: '#EBEBEB' },
          50: { value: '#F2F2F2' },
          10: { value: '#F7F7F7' },
        },
        white: {
          value: '#FFF',
        },
        whiteAlpha: {
          100: { value: '#FFF' },
          90: { value: 'rgba(255, 255, 255, 0.9)' },
          80: { value: 'rgba(255, 255, 255, 0.8)' },
          70: { value: 'rgba(255, 255, 255, 0.7)' },
          60: { value: 'rgba(255, 255, 255, 0.6)' },
          50: { value: 'rgba(255, 255, 255, 0.5)' },
          40: { value: 'rgba(255, 255, 255, 0.4)' },
          30: { value: 'rgba(255, 255, 255, 0.3)' },
          20: { value: 'rgba(255, 255, 255, 0.2)' },
          10: { value: 'rgba(255, 255, 255, 0.1)' },
        },
        black: {
          value: '#000',
        },
        blackAlpha: {
          100: { value: '#000' },
          90: { value: 'rgba(0, 0, 0, 0.9)' },
          80: { value: 'rgba(0, 0, 0, 0.8)' },
          70: { value: 'rgba(0, 0, 0, 0.7)' },
          60: { value: 'rgba(0, 0, 0, 0.6)' },
          50: { value: 'rgba(0, 0, 0, 0.5)' },
          40: { value: 'rgba(0, 0, 0, 0.4)' },
          30: { value: 'rgba(0, 0, 0, 0.3)' },
          20: { value: 'rgba(0, 0, 0, 0.2)' },
          10: { value: 'rgba(0, 0, 0, 0.1)' },
          5: { value: 'rgba(0, 0, 0, 0.05)' },
          3: { value: 'rgba(0, 0, 0, 0.03)' },
        },
        /** ================== neutral end =========================== */
        /** ================== functional start =========================== */
        yellow: {
          5: { value: '#FFD664' },
          6: { value: 'rgba(255, 214, 100, 0.06)' },
          10: { value: 'rgba(255, 144, 0, 0.10)' },
          100: { value: '#FF9000' },
        },
        blue: {
          100: { value: '#2F65E2' },
          50: { value: 'rgba(47, 101, 226, 0.5)' },
        },
        green: {
          100: { value: '#00B600' },
          90: { value: '#00BC00' },
          50: { value: 'rgba(0, 182, 0, 0.5)' },
          10: { value: 'rgba(0, 182, 0, 0.1)' },
        },
        red: {
          110: { value: '#FF0015' },
          100: { value: '#FF3B3B' },
          80: { value: 'rgba(255, 59, 59, 0.8)' },
          50: { value: 'rgba(255, 59, 59, 0.5)' },
          10: { value: 'rgba(255, 59, 59, 0.1)' },
        },
        orange: {
          100: { value: '#FF9D00' },
          50: { value: 'rgba(255, 157, 0, 0.5)' },
          10: { value: 'rgba(255, 157, 0, 0.1)' },
        },
        /** ================== functional end =========================== */
      },
      radii: {
        xl: { value: '0.625rem' },
        '2xl': { value: '0.75rem' },
        '3xl': { value: '0.875rem' },
        '4xl': { value: '1rem' },
      },
    },

    semanticTokens: {
      colors: {
        brand: {
          solid: { value: '{colors.brand.100}' },
          contrast: { value: '{colors.white}' },
          fg: {
            value: {
              _light: '{colors.brand.100}',
              _dark: '{colors.brand.30}',
            },
          },
          muted: { value: '{colors.brand.10}' },
          subtle: { value: '{colors.brand.6}' },
          emphasized: { value: '{colors.brand.30}' },
          focusRing: { value: '{colors.brand.100}' },
        },

        bg: {
          DEFAULT: {
            value: {
              _light: '{colors.white}',
              _dark: '{colors.black}',
            },
          },
          app: {
            value: {
              _light: '{colors.grey.10}',
              _dark: '{colors.grey.97}',
            },
          },
          panel: {
            value: {
              _light: '{colors.white}',
              _dark: '{colors.black}',
            },
          },
          panelAlpha: {
            value: {
              _light: '{colors.whiteAlpha.50}',
              _dark: '{colors.blackAlpha.50}',
            },
          },
          subtle: {
            value: {
              _light: '{colors.grey.10}',
              _dark: '{colors.grey.10}',
            },
          },
          muted: {
            value: {
              _light: '{colors.blackAlpha.3}',
              _dark: '{colors.whiteAlpha.10}',
            },
          },
          // emphasized: {
          //   value: {
          //     _light: '{colors.whiteAlpha.50}',
          //     _dark: '{colors.blackAlpha.50}',
          //   },
          // },
          warning: {
            value: '{colors.yellow.10}',
          },
          error: {
            value: {
              _light: '{colors.red.10}',
              _dark: '{colors.red.10}',
            },
          },
        },

        fg: {
          DEFAULT: {
            value: {
              _light: '{colors.grey.97}',
              _dark: '{colors.grey.97}',
            },
          },
          muted: {
            value: {
              _light: '{colors.grey.95}',
              _dark: '{colors.grey.95}',
            },
          },
          subtle: {
            value: {
              _light: '{colors.grey.92}',
              _dark: '{colors.grey.92}',
            },
          },
          warning: {
            value: '{colors.yellow.100}',
          },
          error: {
            value: {
              _light: '{colors.red.100}',
              _dark: '{colors.red.100}',
            },
          },
        },

        border: {
          DEFAULT: {
            value: {
              _light: '{colors.grey.50}',
              _dark: '{colors.whiteAlpha.10}',
            },
          },
          line: {
            value: {
              _light: '{colors.grey.88}',
              _dark: '{colors.whiteAlpha.10}',
            },
          },
          muted: {
            value: {
              _light: '{colors.grey.64}',
              _dark: '{colors.grey.97}',
            },
          },
          subtle: {
            value: {
              _light: '{colors.grey.10}',
              _dark: '{colors.grey.97}',
            },
          },
        },
      },
    },
  },
  globalCss: {
    ':root': {
      '--text-scale-factor': '0px',
    },
    // global focus outline width
    ':where(:focus-visible)': {
      outlineWidth: '0 !important',
    },
  },
});
